'use client';

import { IHeader } from '@/.generated';
import { useEffect, useState } from 'react';
import { desktopHeaderVariants } from '../Header.styles';
import { getCSLPAttributes } from '@/utils/type-guards';
import { HeaderNavigation } from './HeaderNavigation';
import { hasMegaMenuContent } from '../hasMegaMenuContent';
import { visibleNavLevelOne } from '../visibleNavLinks';
import { getBreakpoint, useCurrentScreenType } from '@/lib/hooks/useScreenType';
import { trackAdobeClick } from '@/utils/adobe';

export const DesktopHeader = (props: IHeader) => {
  const { screenType } = useCurrentScreenType();
  const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null);
  const { navBar, navBarContainer, menuList, menuItem } = desktopHeaderVariants({
    menuVisible: false,
  });
  const navLevelOneItems = visibleNavLevelOne(props.nav_level_one);
  const hasLevelOneNavigation = navLevelOneItems.length > 0;

  const isDesktop = getBreakpoint(screenType || 'desktop') >= getBreakpoint('desktop-lg');

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element;
      const clickedLi = target.closest('li');
      const clickedInsideMenu = target.closest('[data-nav-menu]');
      if (!clickedLi && !clickedInsideMenu) {
        setActiveMenuIndex(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveMenuIndex(null);
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!isDesktop) return null;
  if (!hasLevelOneNavigation) return null;

  return (
    <nav className={navBar()}>
      <div className={navBarContainer()}>
        <ul className={menuList()}>
          {navLevelOneItems.map((levelOneItem, index) => {
            const { menuItemTitle } = desktopHeaderVariants({
              menuVisible: activeMenuIndex === index,
            });
            const hasMenuContent = hasMegaMenuContent(levelOneItem);
            return (
              <li key={index} className={menuItem()}>
                <button
                  className={menuItemTitle()}
                  onClick={(e) => {
                    if (!hasMenuContent) return;
                    trackAdobeClick({ clickName: levelOneItem.nav_level_one_title ?? '' }, e);
                    const isClosing = activeMenuIndex === index;
                    setActiveMenuIndex(isClosing ? null : index);
                    // Drop focus when closing so the trigger doesn't retain a focus/active style after the menu hides
                    if (isClosing) e.currentTarget.blur();
                  }}
                  aria-expanded={activeMenuIndex === index}
                  tabIndex={hasMenuContent ? undefined : -1}
                  {...getCSLPAttributes(levelOneItem.$?.nav_level_one_title)}
                >
                  {levelOneItem.nav_level_one_title}
                </button>

                <HeaderNavigation
                  {...levelOneItem}
                  myAccountLoginReference={props.my_account_login_reference?.[0] ?? undefined}
                  activeMenuIndex={activeMenuIndex}
                  currentIndex={index}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};
